import React, { useState, useEffect, useRef, useImperativeHandle, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ListSegment } from '../../services/accountService';

const SelectEditor = (props) => {
    const {
        value,
        onValueChange,
        listSegmentsEditor = [],
        listSegments = [],
        editorKey,
        agGridRef,
        onOpenNewOptionModal,
        setisListNameUpdated,
        isListNameUpdated,
        onSegmentsFetched,
        getListValue,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState(value ?? '');
    const [currentListId, setCurrentListId] = useState(props.listId);
    const [currentSegments, setCurrentSegments] = useState(listSegments);
    const [selectedOptionId, setSelectedOptionId] = useState(null);

    const [searchText, setSearchText] = useState('');
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const previousValueRef = useRef(value);
    const previousListIdRef = useRef(props.listId);
    const [portalContainer, setPortalContainer] = useState(null);

    const shouldSkipNextUpdate = useRef(false);
    const clearedValueRef = useRef('');

    // On-demand segment fetching state
    const [segmentsLoading, setSegmentsLoading] = useState(false);
    const [fetchedSegments, setFetchedSegments] = useState([]);
    const lastFetchedListIdRef = useRef(null);

 
    const pendingParentUpdateRef = useRef(null);

    const isFetchingSegmentsRef = useRef(false);

   
    const resolveCurrentListId = () => {
        const nodeData = props.node?.data;
        let listId = nodeData?.listId
            || nodeData?.listID
            || nodeData?.listMasterID;

        if (listId && !isNaN(Number(listId))) return Number(listId);

        if (currentListId && !isNaN(Number(currentListId))) return Number(currentListId);
        if (props.listId && !isNaN(Number(props.listId))) return Number(props.listId);

        const listName = nodeData?.listName;
        if (listName && Array.isArray(getListValue)) {
            const matched = getListValue.find(
                l => l.listName?.trim().toLowerCase() === listName.trim().toLowerCase()
            );
            if (matched) {
                const resolvedId = matched.listID ?? matched.listMasterID ?? matched.id;
                if (resolvedId && !isNaN(Number(resolvedId))) return Number(resolvedId);
            }
        }

        return null;
    };

   
    const fetchSegmentsForList = async (listId) => {
        if (!listId || isNaN(Number(listId))) {
            setFetchedSegments([]);
            return [];
        }
        try {
            setSegmentsLoading(true);
            const segments = await ListSegment(Number(listId));
            const result = Array.isArray(segments) ? segments : [];

            
            isFetchingSegmentsRef.current = true;

            setFetchedSegments(result);
            lastFetchedListIdRef.current = Number(listId);

            
            pendingParentUpdateRef.current = { listId: Number(listId), segments: result };

            return result;
        } catch (error) {
            console.error('Error fetching list segments:', error);
            setFetchedSegments([]);
            return [];
        } finally {
            setSegmentsLoading(false);
        }
    };

   
    useEffect(() => {
        if (!isOpen && pendingParentUpdateRef.current) {
            const { listId, segments } = pendingParentUpdateRef.current;
            pendingParentUpdateRef.current = null;
            if (onSegmentsFetched) {
                onSegmentsFetched(listId, segments);
            }
        }
    }, [isOpen, onSegmentsFetched]);

   
    useEffect(() => {
        const newListId = props.listId;
        if (newListId !== lastFetchedListIdRef.current) {
            setFetchedSegments([]);
            lastFetchedListIdRef.current = null;
        }
        setCurrentListId(newListId);
    }, [props.listId]);

    useEffect(() => {
        if (isListNameUpdated === true) {
            setisListNameUpdated(false);
            setFetchedSegments([]);
            lastFetchedListIdRef.current = null;
            setTimeout(() => {
                setLocalValue('');
                previousValueRef.current = '';
                setSelectedOptionId(null);
                if (onValueChange) {
                    onValueChange('', null);
                }
            }, 0);
        }
    }, [isListNameUpdated, setisListNameUpdated, onValueChange]);

    useEffect(() => {
        const container = document.createElement('div');
        container.id = 'select-editor-portal';
        container.style.position = 'fixed';
        container.style.zIndex = '10000';
        document.body.appendChild(container);
        setPortalContainer(container);
        return () => {
            if (container && document.body.contains(container)) {
                document.body.removeChild(container);
            }
        };
    }, []);

    useEffect(() => {
        setCurrentSegments(listSegments);
    }, [listSegments]);

   
    const availableOptions = useMemo(() => {
        let source;
        if (fetchedSegments.length > 0) {
            source = fetchedSegments;
        } else if (Array.isArray(currentSegments) && currentSegments.length > 0) {
            source = currentSegments;
        } else if (Array.isArray(listSegmentsEditor) && listSegmentsEditor.length > 0) {
            source = listSegmentsEditor;
        } else {
            source = [];
        }
        return source.map(item => {
            const name = item?.listSegment ?? item?.name ?? item?.value ?? String(item);
            const id = item?.listID ?? item?.id ?? item?.dataId ?? null;
            return { id, name, value: String(name), dataId: id };
        });
    }, [fetchedSegments, currentSegments, listSegmentsEditor]);

    const filteredOptions = useMemo(() => {
        if (!searchText.trim()) return availableOptions;
        const term = searchText.trim().toLowerCase();
        return availableOptions.filter(opt =>
            String(opt.name || '').toLowerCase().includes(term)
        );
    }, [availableOptions, searchText]);

    
    useEffect(() => {
       
        if (isFetchingSegmentsRef.current) {
            isFetchingSegmentsRef.current = false;
           
            const found = availableOptions.find(opt => String(opt.value) === String(localValue));
            setSelectedOptionId(found?.id ?? null);
            return;
        }

        if (shouldSkipNextUpdate.current) {
            shouldSkipNextUpdate.current = false;
            return;
        }

        if (clearedValueRef.current === '' && localValue === '') {
            return;
        }

        const nextValue = value ?? '';
        setLocalValue(nextValue);
        previousValueRef.current = nextValue;

        const found = availableOptions.find(opt => String(opt.value) === String(nextValue));
        setSelectedOptionId(found?.id ?? null);

       
        setIsOpen(false);
    }, [editorKey, value, availableOptions, localValue]);

   
    useEffect(() => {
        if (isOpen && triggerRef.current && portalContainer) {
            const rect = triggerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const DROPDOWN_HEIGHT = Math.min(340, availableOptions.length * 40 + 94);

            if (spaceBelow < DROPDOWN_HEIGHT && rect.top > DROPDOWN_HEIGHT) {
                portalContainer.style.bottom = `${viewportHeight - rect.top + 5}px`;
                portalContainer.style.top = 'auto';
            } else {
                portalContainer.style.top = `${rect.bottom + 5}px`;
                portalContainer.style.bottom = 'auto';
            }
            portalContainer.style.left = `${rect.left}px`;
            portalContainer.style.minWidth = `${Math.max(rect.width, 200)}px`;
            portalContainer.style.display = 'block';
        } else if (portalContainer) {
            portalContainer.style.display = 'none';
        }
    }, [isOpen, portalContainer, availableOptions.length]);

  
    useEffect(() => {
        if (isOpen) {
            const handleReposition = () => {
                if (triggerRef.current && portalContainer) {
                    const rect = triggerRef.current.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    const spaceBelow = viewportHeight - rect.bottom;
                    const DROPDOWN_HEIGHT = Math.min(340, availableOptions.length * 40 + 94);

                    if (spaceBelow < DROPDOWN_HEIGHT && rect.top > DROPDOWN_HEIGHT) {
                        portalContainer.style.bottom = `${viewportHeight - rect.top + 5}px`;
                        portalContainer.style.top = 'auto';
                    } else {
                        portalContainer.style.top = `${rect.bottom + 5}px`;
                        portalContainer.style.bottom = 'auto';
                    }
                    portalContainer.style.left = `${rect.left}px`;
                    portalContainer.style.minWidth = `${Math.max(rect.width, 200)}px`;
                }
            };
            window.addEventListener('scroll', handleReposition, true);
            window.addEventListener('resize', handleReposition);
            return () => {
                window.removeEventListener('scroll', handleReposition, true);
                window.removeEventListener('resize', handleReposition);
            };
        }
    }, [isOpen, portalContainer, availableOptions.length]);

   
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen &&
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Clear search on close
    useEffect(() => {
        if (!isOpen) setSearchText('');
    }, [isOpen]);

    // Focus search on open
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            requestAnimationFrame(() => searchInputRef.current?.focus());
        }
    }, [isOpen]);

    useEffect(() => {
        if (value !== previousValueRef.current) {
            if (!shouldSkipNextUpdate.current && clearedValueRef.current !== '') {
                setLocalValue(value ?? '');
                previousValueRef.current = value;
                const found = availableOptions.find(opt => String(opt.value) === String(value));
                setSelectedOptionId(found?.id ?? null);
            }
        }
    }, [value, availableOptions]);

    useEffect(() => {
        if (currentListId !== previousListIdRef.current) {
            setLocalValue('');
            setSelectedOptionId(null);
            previousValueRef.current = '';
            setIsOpen(false);
            setFetchedSegments([]);
            lastFetchedListIdRef.current = null;
            if (onValueChange) {
                onValueChange('', null);
            }
            previousListIdRef.current = currentListId;
        }
    }, [currentListId, onValueChange]);

   
    const handleDropdownOpen = async () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }

        setIsOpen(true);

        const listId = resolveCurrentListId();

        if (listId && lastFetchedListIdRef.current !== listId) {
            await fetchSegmentsForList(listId);
        }
    };

    const handleSelect = (option) => {
        if (option.value === '__new__') {
            const currentRowData = props.node?.data;
            if (onOpenNewOptionModal) {
                onOpenNewOptionModal(props.node, currentRowData);
            }
            setIsOpen(false);
            return;
        }

        const selectedValue = option.value;
        const selectedId = option.id;

        clearedValueRef.current = selectedValue;
        setLocalValue(selectedValue);
        setSelectedOptionId(selectedId);
        setIsOpen(false);

        if (onValueChange) {
            onValueChange(selectedValue, selectedId);
        }
    };

    const handleNewOption = () => {
        setIsOpen(false);
        if (onOpenNewOptionModal) {
            const currentRowData = props.node?.data;
            onOpenNewOptionModal(props.node, currentRowData);
        }
    };

    const getDisplayValue = () => {
        if (segmentsLoading) return 'Loading...';
        if (!localValue || localValue === '') return 'Select an option';
        return localValue;
    };

    useImperativeHandle(agGridRef, () => ({
        getValue: () => localValue,
        getValueId: () => selectedOptionId,
        refresh: (params) => {
            const nextValue = params?.value ?? '';
            const nextListId = params?.listId;
            const nextSegments = params?.listSegments;

            setCurrentListId(nextListId);
            setCurrentSegments(nextSegments);
            setFetchedSegments([]);
            lastFetchedListIdRef.current = null;

            if (shouldSkipNextUpdate.current) {
                shouldSkipNextUpdate.current = false;
                clearedValueRef.current = '';
                setLocalValue('');
                previousValueRef.current = '';
                setSelectedOptionId(null);
            } else {
                setLocalValue(nextValue);
                previousValueRef.current = nextValue;
                const found = Array.isArray(nextSegments)
                    ? nextSegments.find(s => String(s?.listSegment ?? s?.name ?? s?.value) === String(nextValue))
                    : null;
                setSelectedOptionId(found?.listID ?? found?.id ?? null);
            }

            setIsOpen(false);
            return true;
        },
        isCancelBeforeStart: () => false,
        isCancelAfterEnd: () => false
    }), [localValue, selectedOptionId]);

    const renderDropdown = () => {
        if (!isOpen || !portalContainer) return null;

        return ReactDOM.createPortal(
            <div
                ref={dropdownRef}
                style={{
                    position: 'fixed',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    maxHeight: '340px',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 10000,
                    overflow: 'hidden',
                    width: '400px'
                }}
            >
                {/* Search input */}
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search options..."
                        style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                            outline: 'none'
                        }}
                    />
                </div>

                {/* Options list */}
                <div style={{ flex: 1, overflowY: 'auto', maxHeight: '250px' }}>
                    {segmentsLoading ? (
                        <div style={{
                            padding: '20px 12px',
                            fontSize: '14px',
                            color: '#666',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            <span style={{
                                display: 'inline-block',
                                width: '16px',
                                height: '16px',
                                border: '2px solid #e0e0e0',
                                borderTopColor: '#2e6f9f',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                            }} />
                            Loading options...
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div style={{
                            padding: '14px 12px',
                            fontSize: '14px',
                            color: '#999',
                            textAlign: 'center'
                        }}>
                            {availableOptions.length === 0 ? 'No options available' : 'No matching options'}
                        </div>
                    ) : (
                        filteredOptions.map((option, index) => (
                            <div
                                key={option.dataId ?? option.value ?? index}
                                onClick={() => handleSelect(option)}
                                style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#333',
                                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    backgroundColor: localValue === option.value ? '#e3f2fd' : 'transparent',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = localValue === option.value ? '#e3f2fd' : 'transparent';
                                }}
                            >
                                {option.name}
                            </div>
                        ))
                    )}
                </div>

                {/* Create new option */}
                <div
                    onClick={handleNewOption}
                    style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#2e6f9f',
                        fontWeight: 'bold',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        transition: 'background-color 0.2s',
                        flexShrink: 0
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                    + Create new Option
                </div>
            </div>,
            portalContainer
        );
    };

    return (
        <>
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div
                    ref={triggerRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        padding: '4px 8px',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box',
                        border: '1px solid transparent',
                        opacity: segmentsLoading ? 0.7 : 1
                    }}
                    onClick={handleDropdownOpen}
                >
                    <span style={{
                        flex: 1,
                        color: localValue ? '#333' : '#999',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {getDisplayValue()}
                    </span>
                    <span style={{
                        fontSize: '10px',
                        color: '#999',
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        marginLeft: '8px'
                    }}>▼</span>
                </div>
            </div>
            {renderDropdown()}
        </>
    );
};

export default SelectEditor;